import * as React from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import {v4 as uuidv4} from 'uuid';
import classNames from 'classnames';
import {MultiMenu, MultiMenuItem} from '@deviceinsight/ng-ui-components';
import {Button, Icon} from '@deviceinsight/ng-ui-basic-components';
import {AssetGroup, Asset} from '@deviceinsight/ng-ui-api-client';
import {DeviceElement, LogicOperator, RuleConditionState, UiCondition} from '../../../modules/ruleengine/types';
import DeviceAndActionIcon from '../../common/DeviceAndActionIcon';
import RuleAsset from '../assetselection/RuleAsset';
import ConditionElement from './ConditionElement';
import ConditionElementTarget from './ConditionElementTarget';
import {createCondition, getAssetDeviceType, initConditionState, validateConditionState} from '../ruleEditHelpers';
import {
	ACTION_TYPE_DEVICE,
	CompareOperator,
	OPERATOR_AND,
	OPERATOR_OR
} from '../../../modules/ruleengine/config/consts';
import TriggerOptionalSettings from './TriggerOptionalSettings';
import DeleteButton from '../../common/DeleteButton';
type Props = {
	condition: UiCondition;
	index: number;
	onChange: (arg0: UiCondition, arg1: number) => void;
	assetGroup: AssetGroup;
	onRemove: (conditionIndex: number) => void;
	isTrigger: boolean;
	isChild?: boolean;
	isAddable: boolean;
	canEdit: boolean;
};
export default class RuleConditionEdit extends React.PureComponent<Props, RuleConditionState> {
	state: RuleConditionState = {
		id: undefined,
		asset: undefined,
		element: undefined,
		elementTarget: undefined,
		online: undefined,
		color: undefined,
		status: undefined,
		pressed: undefined,
		frequency: undefined,
		compareOperator: undefined,
		counter: undefined,
		duration: undefined
	};

	componentDidMount(): any {
		const {condition} = this.props;
		initConditionState(condition).then((state) => this.setState(state));
	}

	onAssetSelect = (asset: Asset) => {
		this.validateAndCommit({
			asset,
			element: undefined,
			elementTarget: undefined,
			online: undefined,
			color: undefined,
			status: undefined,
			pressed: undefined,
			frequency: undefined,
			compareOperator: CompareOperator.equal
		});
	};
	onElementSelect = (element?: DeviceElement) => {
		const {asset} = this.state;
		this.validateAndCommit({
			asset,
			element,
			elementTarget: undefined,
			online: undefined,
			color: undefined,
			status: undefined,
			pressed: undefined,
			frequency: undefined,
			counter: undefined,
			duration: undefined,
			compareOperator: CompareOperator.equal
		});
	};
	onElementTargetSelect = (partialState: Partial<RuleConditionState>) => {
		this.validateAndCommit({
			online: undefined,
			color: undefined,
			status: undefined,
			pressed: undefined,
			frequency: undefined,
			...partialState
		});
	};
	onOptionalSettingsSelect = (partialState: Partial<RuleConditionState>) => {
		this.validateAndCommit({...partialState});
	};
	onChangeCompareOperator = (compareOperator: CompareOperator) => {
		this.validateAndCommit({
			compareOperator
		});
	};
	buildCondition = (uuid: string): UiCondition & {type?: string} => {
		const {
			id,
			asset,
			elementTarget,
			online,
			color,
			status,
			pressed,
			frequency,
			compareOperator,
			counter,
			duration
		} = this.state;
		const isValid = validateConditionState(this.state);
		const type = getAssetDeviceType(asset);
		return {
			id,
			assetUrn: asset ? asset.urn : '',
			dataPoint: elementTarget ? elementTarget.dataPoint : undefined,
			online,
			color,
			frequency,
			status,
			pressed,
			compareOperator,
			nestedConditions: undefined,
			logicOperator: undefined,
			type,
			counter,
			duration,
			metadata: {
				uuid,
				isValid
			}
		};
	};
	addCondition = (operator: LogicOperator) => {
		const {
			onChange,
			condition: {
				metadata: {uuid}
			},
			index
		} = this.props;
		const newSimpleCondition = createCondition(undefined); // This is the new one to add as a child

		const formerSingleCondition = this.buildCondition(uuidv4());
		const wrapperComplexCondition = createCondition(operator);
		wrapperComplexCondition.nestedConditions = [formerSingleCondition, newSimpleCondition];
		wrapperComplexCondition.metadata.uuid = uuid;
		onChange(wrapperComplexCondition, index);
	};
	validateAndCommit = (modifiedState: Partial<RuleConditionState>) => {
		const {
			onChange,
			index,
			condition: {
				metadata: {uuid}
			}
		} = this.props;
		this.setState({...modifiedState}, () => {
			onChange(this.buildCondition(uuid), index);
		});
	};

	render() {
		const {
			assetGroup,
			onRemove,
			index,
			condition: {
				metadata: {isValid}
			},
			isTrigger,
			isChild,
			isAddable,
			canEdit
		} = this.props;
		const {asset, element, elementTarget} = this.state;
		const conditionClass = classNames({
			rule_condition_edit: true,
			valid: isValid
		});
		return (
			<div className={conditionClass}>
				<div className="condition-items">
					<DeviceAndActionIcon asset={asset} type={ACTION_TYPE_DEVICE} />
					<RuleAsset
						assetGroup={assetGroup}
						onAssetSelect={this.onAssetSelect}
						asset={asset}
						canEdit={canEdit}
					/>
					{asset && (
						<ConditionElement
							asset={asset}
							canEdit={canEdit}
							onElementSelect={this.onElementSelect}
							selectedElement={element}
						/>
					)}
					{element ? (
						<ConditionElementTarget
							onElementTargetSelect={this.onElementTargetSelect}
							onChangeCompareOperator={this.onChangeCompareOperator}
							currentConditionState={this.state}
							isTrigger={isTrigger}
							element={element}
							canEdit={canEdit}
						/>
					) : (
						<div className="rule-target" />
					)}
					{elementTarget && isTrigger ? (
						<TriggerOptionalSettings
							canEdit={canEdit}
							elementTarget={elementTarget}
							counter={this.state.counter}
							duration={this.state.duration}
							onChange={this.onOptionalSettingsSelect}
						/>
					) : null}
				</div>
				{canEdit && (
					<div className="rule-functions">
						<DeleteButton data-testid="remove-condition-button" onClick={() => onRemove(index)} />
						{isAddable &&
							!isChild &&
							(isTrigger ? (
								<Button naked onClick={() => this.addCondition(OPERATOR_OR as LogicOperator)}>
									<Icon add />
								</Button>
							) : (
								<MultiMenu
									menuButton={
										<Button naked>
											<Icon add />
										</Button>
									}>
									<MultiMenuItem onClick={() => this.addCondition(OPERATOR_AND as LogicOperator)}>
										<Trans i18nKey={`ruleengine.AND`}>AND</Trans>
									</MultiMenuItem>
									<MultiMenuItem onClick={() => this.addCondition(OPERATOR_OR as LogicOperator)}>
										<Trans i18nKey={`ruleengine.OR`}>OR</Trans>
									</MultiMenuItem>
								</MultiMenu>
							))}
					</div>
				)}
			</div>
		);
	}
}
